import {
  SxProps,
  Theme,
  TooltipProps,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import { PropsWithChildren, useState } from 'react';
import { SetOptional } from 'type-fest';

import { Tooltip } from '../poppers/Tooltip/Tooltip';

export type TypographyClampedProps = TypographyProps &
  PropsWithChildren<{
    lines?: number;
    tooltipProps?: SetOptional<TooltipProps, 'open' | 'children'>;
    /**
     * When true, reserves consistent vertical space for the text element regardless of content length.
     * This ensures uniform heights in grid layouts by applying minHeight even when content is shorter.
     */
    reserveSpace?: boolean;
  }>;

export function TypographyClamped({
  lines,
  onMouseEnter: onMouseEnterProp,
  onMouseLeave: onMouseLeaveProp,
  tooltipProps,
  children,
  reserveSpace = false,
  ...props
}: TypographyClampedProps) {
  // Always call hooks at the top level
  const theme = useTheme();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // If no lines specified, don't clamp at all - return early but after hooks
  if (lines === undefined) {
    const typographyProps: TypographyProps = {
      ...props,
      onMouseEnter: onMouseEnterProp,
      onMouseLeave: onMouseLeaveProp,
    };
    return (
      <Typography variant="body-xs" {...typographyProps}>
        {children}
      </Typography>
    );
  }

  let useClamp = false;
  if (
    // if line-clamp and box-orient are supported
    // we can use them to clamp the text
    typeof window !== 'undefined' &&
    CSS?.supports?.('-webkit-line-clamp', '1') &&
    CSS?.supports?.('-webkit-box-orient', 'vertical') &&
    CSS?.supports?.('display', '-webkit-box')
  ) {
    useClamp = true;
  }

  let variant = props.variant;
  if (!variant || variant === 'inherit') {
    variant = 'title-xl';
  }

  // determine the line height of the text variant and compute minHeight for `lines`
  const lh = theme.typography[variant].lineHeight as number | string;
  let minHeight: string;
  if (typeof lh === 'number') {
    minHeight = `${lh * lines}em`;
  } else if (typeof lh === 'string' && lh.endsWith('px')) {
    const num = parseFloat(lh);
    minHeight = `${num * lines}px`;
  } else {
    // fallback assuming ~1.5 line-height
    minHeight = `${1.5 * lines}em`;
  }

  const style: SxProps<Theme> = useClamp
    ? {
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        whiteSpace: 'pre-wrap',
        ...(reserveSpace ? { minHeight } : {}),
      }
    : reserveSpace
      ? {
          minHeight,
          maxHeight: minHeight,
          overflow: 'hidden',
        }
      : {
          maxHeight: minHeight,
          overflow: 'hidden',
        };

  const onMouseEnter: TypographyProps['onMouseEnter'] = (event) => {
    if (!!tooltipProps) {
      const showTooltip =
        event.currentTarget.scrollHeight > event.currentTarget.clientHeight;

      if (showTooltip) {
        setTooltipOpen(true);
      }
    }

    onMouseEnterProp?.(event);
  };

  const onMouseLeave: TypographyProps['onMouseLeave'] = (event) => {
    setTooltipOpen(false);
    onMouseLeaveProp?.(event);
  };

  const typographyProps: TypographyProps = {
    ...props,
    sx: { ...style, ...props.sx },
    onMouseEnter: tooltipProps ? onMouseEnter : onMouseEnterProp,
    onMouseLeave: tooltipProps ? onMouseLeave : onMouseLeaveProp,
  };

  if (!tooltipProps) {
    return (
      <Typography variant="body-xs" {...typographyProps}>
        {children}
      </Typography>
    );
  }

  return (
    <Tooltip open={tooltipOpen} placement="right" {...tooltipProps}>
      <Typography variant="body-xs" {...typographyProps}>
        {children}
      </Typography>
    </Tooltip>
  );
}
